<template>
  <div :id="id" :class="wrapperClasses">
    <div class="container mx-auto flex flex-col items-center space-y-2 px-6 md:px-12 lg:flex-row lg:space-x-10">
      <div class="flex w-full flex-col lg:w-2/5">
        <div
          v-t="$cfg.newsletterTitle"
          :class="textColor ? textColor : 'text-white'"
          class="mb-4 text-3xl font-bold text-white lg:text-5xl"
        ></div>
        <div
          :class="textColor ? textColor : 'text-white'"
          class="text-base font-medium leading-tight text-white"
          v-html-safe="$cfg.newsletterSubtitle"
        ></div>
      </div>
      <div class="pt:mt-0 flex w-full grow pt-8 lg:w-3/5">
        <VcInput
          v-model="emailAddress"
          class="max-h-14 grow lg:max-w-[450px]"
          no-border
          input-class="text-white placeholder:text-white"
          bg-class="bg-[color:var(--color-newsletter-input-bg)]"
          :placeholder="$cfg.newsletterPlaceholder"
        />
        <VcIcon
          class="-mt-1 ml-3 cursor-pointer text-primary"
          @click="submitNewsLetter()"
          name="veld-button"
          :size="70"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import axios from "axios";
import CryptoJS from "crypto-js";
import OAuth from "oauth-1.0a";
import { ref, computed } from "vue";
import { useI18n } from "vue-i18n";
import { useThemeContext } from "@/core/composables";
import { useNotifications } from "@/shared/notification";

interface IProps {
  id: string;
  background?: string;
  textColor?: string;
}

const props = defineProps<IProps>();

const wrapperClasses = computed(() => {
  return [props.background ? props.background : "bg-[color:var(--color-footer-top-bg)]", "py-6 lg:py-10"];
});
const { t } = useI18n();
const notifications = useNotifications();
const { themeContext } = useThemeContext();
const emailAddress = ref("");

async function submitNewsLetter() {
  if (!emailAddress.value) {
    notifications.error({
      text: t("custom.newsletter.empty"),
      duration: 5000,
      single: true,
    });
    return;
  }

  // OAUth 1.0a intergration for spotler
  const oauth = new OAuth({
    version: "1.0",
    consumer: {
      key: themeContext.value.settings.spotlerKey!,
      secret: themeContext.value.settings.spotlerSecret!,
    },
    hash_function: (base_string: string, key: string) =>
      CryptoJS.HmacSHA1(base_string, key).toString(CryptoJS.enc.Base64),
    signature_method: "HMAC-SHA1",
  });

  const request_data = {
    url: "https://restapi.mailplus.nl/integrationservice/subscription/subscribe",
    method: "POST",
  };

  const oauthHeaders = oauth.toHeader(oauth.authorize(request_data));

  // Spotler body
  const body = {
    properties: {
      email: emailAddress.value,
    },
  };

  const spotlerApi = axios.create({
    baseURL: "https://restapi.mailplus.nl/integrationservice",
    headers: {
      ...oauthHeaders,
      "Content-Type": "application/json",
    },
  });

  try {
    const response = await spotlerApi.post("/subscription/subscribe", body);

    if (response.status === 204) {
      notifications.success({
        text: t("custom.newsletter.success"),
        duration: 10000,
        single: true,
      });
      emailAddress.value = "";
    }
  } catch (error: any) {
    console.error("Spotler error response:", error.response?.data);
    notifications.error({
      text: error.response?.data?.message || "Er ging iets mis",
      duration: 10000,
      single: true,
    });
  }
}
</script>
